import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './Dashboard.css';
import { logout } from '../../services/authService';
import DashboardPage from './DashboardPage';
import VehicleManagement from './VehicleManagement';
import DriverManagement from './DriverManagement';
import TripManagement from './TripManagement';
import BillGeneration from './BillGeneration';
import Tracking from './Tracking';
import CustomerManagement from './CustomerManagement';
import AgencyManagement from './AgencyManagement';
import UserManagement from './UserManagement';
import BookingRequests from './BookingRequests';
import ReportsAnalytics from './ReportsAnalytics';
import DocumentManagement from './DocumentManagement';
import SupportIssueLogs from './SupportIssueLogs';

const Dashboard = ({ onLogout }) => {
  const navigate = useNavigate();
  // Load activeMenu from localStorage, default to 'dashboard' if not set
  const [activeMenu, setActiveMenu] = useState(() => {
    const savedMenu = localStorage.getItem('activeMenu');
    // Reset to dashboard if saved menu is one of the removed menus
    if (savedMenu === 'lr-management' || savedMenu === 'payments-invoices' || savedMenu === 'notifications') {
      return 'dashboard';
    }
    return savedMenu || 'dashboard';
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false);
  const profileDropdownRef = useRef(null);
  const sidebarRef = useRef(null);
  const notificationDropdownRef = useRef(null);

  // Save activeMenu to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('activeMenu', activeMenu);
  }, [activeMenu]);

  const menuItems = {
    main: [
      { id: 'dashboard', label: 'Dashboard', icon: '🏠' },
      { id: 'vehicle-management', label: 'Vehicle Management', icon: '🚚' },
      { id: 'driver-management', label: 'Driver Management', icon: '👤' },
      { id: 'trip-management', label: 'Trip Management', icon: '🛣️' },
      { id: 'bill-generation', label: 'Bill Generation', icon: '📄' },
    ],
    management: [
      { id: 'customer-management', label: 'Customer Management', icon: '👥' },
      { id: 'agency-management', label: 'Transporters Management', icon: '🏢' },
      { id: 'user-management', label: 'User Management', icon: '👤' },
      { id: 'booking-requests', label: 'Booking Requests', icon: '📋' },
    ],
    system: [
      { id: 'reports-analytics', label: 'Reports & Analytics', icon: '📈' },
      { id: 'document-management', label: 'Document Management', icon: '📋' },
      { id: 'support-issue-logs', label: 'Support / Issue Logs', icon: '⚠️' },
    ],
  };

  const menuComponents = {
    'dashboard': DashboardPage,
    'vehicle-management': VehicleManagement,
    'driver-management': DriverManagement,
    'trip-management': TripManagement,
    'bill-generation': BillGeneration,
    'tracking': Tracking,
    'customer-management': CustomerManagement,
    'agency-management': AgencyManagement,
    'user-management': UserManagement,
    'booking-requests': BookingRequests,
    'reports-analytics': ReportsAnalytics,
    'document-management': DocumentManagement,
    'support-issue-logs': SupportIssueLogs,
  };

  // Mock notifications data (temporary until backend implements notification creation)
  const notifications = [
    { id: 1, notification_type: 'warning', title: 'Vehicle Insurance Expiring', message: 'Vehicle TN58AB1234 insurance expiring in 3 days', time: '10 minutes ago', type: 'warning', icon: '⚠️', is_read: false },
    { id: 2, notification_type: 'delay', title: 'Trip Delayed', message: 'Trip #4582 is delayed by 2 hours', time: '45 minutes ago', type: 'delay', icon: '🕐', is_read: false },
    { id: 3, notification_type: 'success', title: 'Trip Completed', message: 'Trip #4580 has been completed successfully', time: '1 hour ago', type: 'success', icon: '✓', is_read: false },
    { id: 4, notification_type: 'info', title: 'New LR Entry', message: 'New LR entry created for customer ABC Logistics', time: '2 hours ago', type: 'info', icon: '📄', is_read: false },
    { id: 5, notification_type: 'payment', title: 'Payment Received', message: 'Payment of ₹45,000 received from XYZ Transport', time: '3 hours ago', type: 'payment', icon: '💳', is_read: false },
    { id: 6, notification_type: 'warning', title: 'Driver License Expiring', message: 'Driver license expiring soon for John Doe', time: '4 hours ago', type: 'warning', icon: '⚠️', is_read: false },
    { id: 7, notification_type: 'info', title: 'Document Uploaded', message: 'Document uploaded for vehicle KA01CD5678', time: '5 hours ago', type: 'info', icon: '📄', is_read: false },
    { id: 8, notification_type: 'info', title: 'New Route Created', message: 'New route created: Mumbai to Delhi', time: '6 hours ago', type: 'info', icon: '🗺️', is_read: false },
  ];

  const unreadCount = notifications.filter(n => !n.is_read).length;

  const handleMarkAllAsRead = () => {
    // Mock function - just close the dropdown
    setNotificationDropdownOpen(false);
  };

  const handleMenuClick = (menuId) => {
    setActiveMenu(menuId);
    // Close mobile menu when a menu item is clicked
    setMobileMenuOpen(false);
    // Also collapse the sidebar so it hides on desktop until user opens it again
    setSidebarCollapsed(true);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const getMenuLabel = (menuId) => {
    const allItems = [...menuItems.main, ...menuItems.management, ...menuItems.system];
    const item = allItems.find(item => item.id === menuId);
    return item ? item.label : 'Dashboard';
  };

  const toggleProfileDropdown = () => {
    setProfileDropdownOpen(!profileDropdownOpen);
  };

  const handleLogoutClick = () => {
    setProfileDropdownOpen(false);
    setShowLogoutConfirm(true);
  };

  const handleLogoutConfirm = () => {
    setShowLogoutConfirm(false);
    // Clear saved menu when logging out
    localStorage.removeItem('activeMenu');
    // Use authService to clear all auth data
    logout();
    if (onLogout) {
      onLogout();
    }
    // Navigate to home page
    navigate('/');
  };

  const handleLogoutCancel = () => {
    setShowLogoutConfirm(false);
  };

  const toggleNotificationDropdown = () => {
    setNotificationDropdownOpen(!notificationDropdownOpen);
    setProfileDropdownOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setProfileDropdownOpen(false);
      }
      if (notificationDropdownRef.current && !notificationDropdownRef.current.contains(event.target)) {
        setNotificationDropdownOpen(false);
      }
      // Close mobile menu when clicking outside
      if (sidebarRef.current && mobileMenuOpen && !sidebarRef.current.contains(event.target)) {
        // Check if click is not on mobile menu button
        const mobileMenuBtn = document.querySelector('.mobile-menu-btn');
        if (mobileMenuBtn && !mobileMenuBtn.contains(event.target)) {
          setMobileMenuOpen(false);
        }
      }
    };

    if (profileDropdownOpen || notificationDropdownOpen || mobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
  };
  }, [profileDropdownOpen, notificationDropdownOpen, mobileMenuOpen]);

  return (
    <div className="dashboard-container">
      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div 
          className={`mobile-overlay ${mobileMenuOpen ? 'active' : ''}`}
          onClick={() => setMobileMenuOpen(false)}
        ></div>
      )}
      
      {/* Top Header Bar */}
      <div className="top-header">
        <div className="header-left">
          <button className="mobile-menu-btn" onClick={toggleMobileMenu} aria-label="Toggle menu">
            <span className={mobileMenuOpen ? 'hamburger open' : 'hamburger'}>
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>
          <h1 className="page-title">
            Nettaa Logistics Service
            {activeMenu !== 'dashboard' && (
              <span className="page-subtitle"> - {getMenuLabel(activeMenu)}</span>
            )}
          </h1>
        </div>
        <div className="header-right">
          <div className="notifications-container" ref={notificationDropdownRef}>
            <button className="notifications-btn" onClick={toggleNotificationDropdown} aria-label="Notifications">
              <span className="notification-icon">🔔</span>
              {unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
            </button>
            {notificationDropdownOpen && (
              <div className="notification-dropdown">
                <div className="notification-dropdown-header">
                  <h3>Notifications</h3>
                  <button 
                    className="mark-all-read-btn" 
                    onClick={handleMarkAllAsRead}
                    disabled={unreadCount === 0}
                  >
                    Mark All Read
                  </button>
                </div>
                <div className="notification-dropdown-list">
                  {notifications.length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                      No notifications
                    </div>
                  ) : (
                    notifications.map((notification) => (
                      <div 
                        key={notification.id} 
                        className={`notification-dropdown-item ${!notification.is_read ? 'unread' : ''}`}
                      >
                        <span className={`notification-dropdown-icon ${notification.type || 'default'}`}>
                          {notification.icon}
                        </span>
                        <div className="notification-dropdown-content">
                          <div className="notification-dropdown-text">
                            <strong>{notification.title || 'Notification'}</strong>
                            <br />
                            {notification.message}
                          </div>
                          <div className="notification-dropdown-time">
                            {notification.time}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <div className="notification-dropdown-footer">
                  <span style={{ color: '#999', fontSize: '0.85rem', fontStyle: 'italic' }}>
                    Notifications page coming soon
                  </span>
                </div>
              </div>
            )}
          </div>
          <div className="user-profile-container" ref={profileDropdownRef}>
            <div className="user-profile" onClick={toggleProfileDropdown}>
            <div className="user-avatar">A</div>
            <div className="user-info">
              <span className="user-name">Admin User</span>
              <span className="user-role">Administrator</span>
            </div>
              <button className={`dropdown-arrow ${profileDropdownOpen ? 'open' : ''}`}>▼</button>
            </div>
            {profileDropdownOpen && (
              <div className="profile-dropdown">
                <button className="dropdown-item logout-item" onClick={handleLogoutClick}>
                  <span className="dropdown-icon">🚪</span>
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Left Sidebar */}
      <div 
        ref={sidebarRef}
        className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${mobileMenuOpen ? 'mobile-open' : ''}`}
      >
        <div className="sidebar-header">
          <div className="logo">
            <div className="logo-icon">🚚</div>
          </div>
          {!sidebarCollapsed && <h2 className="logo-text">Nettaa</h2>}
          <button 
            className="collapse-btn"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          >
            {sidebarCollapsed ? '→' : '←'}
          </button>
        </div>

        <nav className="sidebar-nav">
          {/* MAIN Section */}
          <div className="nav-section">
            {!sidebarCollapsed && <h3 className="nav-section-title">MAIN</h3>}
            <ul className="nav-list">
              {menuItems.main.map((item) => (
                <li key={item.id}>
                  <button
                    className={`nav-item ${activeMenu === item.id ? 'active' : ''}`}
                    onClick={() => handleMenuClick(item.id)}
                    title={sidebarCollapsed ? item.label : ''}
                  >
                    <span className="nav-icon">{item.icon}</span>
                    {!sidebarCollapsed && <span className="nav-label">{item.label}</span>}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* MANAGEMENT Section */}
          <div className="nav-section">
            {!sidebarCollapsed && <h3 className="nav-section-title">MANAGEMENT</h3>}
            <ul className="nav-list">
              {menuItems.management.map((item) => (
                <li key={item.id}>
                  <button
                    className={`nav-item ${activeMenu === item.id ? 'active' : ''}`}
                    onClick={() => handleMenuClick(item.id)}
                    title={sidebarCollapsed ? item.label : ''}
                  >
                    <span className="nav-icon">{item.icon}</span>
                    {!sidebarCollapsed && <span className="nav-label">{item.label}</span>}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* SYSTEM Section */}
          <div className="nav-section">
            {!sidebarCollapsed && <h3 className="nav-section-title">SYSTEM</h3>}
            <ul className="nav-list">
              {menuItems.system.map((item) => (
                <li key={item.id}>
                  <button
                    className={`nav-item ${activeMenu === item.id ? 'active' : ''}`}
                    onClick={() => handleMenuClick(item.id)}
                    title={sidebarCollapsed ? item.label : ''}
                  >
                    <span className="nav-icon">{item.icon}</span>
                    {!sidebarCollapsed && <span className="nav-label">{item.label}</span>}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        {/* User Info at Bottom */}
        <div className="sidebar-footer">
          <div className="user-profile-sidebar">
            <div className="user-avatar-sidebar">A</div>
            {!sidebarCollapsed && (
              <div className="user-details-sidebar">
                <span className="user-name-sidebar">Admin User</span>
                <span className="user-role-sidebar">Administrator</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className={`main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        {(() => {
          const ActiveComponent = menuComponents[activeMenu];
          if (ActiveComponent) {
            return <ActiveComponent />;
          }
          return (
            <div className="content-wrapper">
              <h1 className="coming-soon-title">{getMenuLabel(activeMenu)}</h1>
              <p className="coming-soon-message">is coming soon...</p>
            </div>
          );
        })()}
              </div>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="modal-overlay" onClick={handleLogoutCancel}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Confirm Logout</h3>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to logout?</p>
            </div>
            <div className="modal-footer">
              <button className="modal-button cancel-button" onClick={handleLogoutCancel}>
                No
              </button>
              <button className="modal-button confirm-button" onClick={handleLogoutConfirm}>
                Yes
              </button>
            </div>
          </div>
          </div>
        )}
    </div>
  );
};

export default Dashboard;
